import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Filter,
  RefreshCw,
  ExternalLink,
  Building2,
  CheckCircle2,
  XCircle,
  FileCode
} from 'lucide-react';
import { registrationApi } from '../../services/iso20022Api';
import {
  formatDate,
  getStatusBadgeClass,
  truncate,
  debounce
} from '../../utils/helpers';
import XmlViewerModal from '../../components/iso20022/XmlViewerModal';

const AccountRegistrations = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });
  const [xmlModalOpen, setXmlModalOpen] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);

  const loadRegistrations = async () => {
    try {
      setLoading(true);
      const data = await registrationApi.getRegistrations(filters);
      console.log('[Load Registrations] Count:', data?.length ?? 0);
      setRegistrations(data);
    } catch (error) {
      console.error('[Load Registrations] Error:', error);
      setRegistrations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRegistrations();
  }, [filters]);

  const handleFilterChange = debounce((name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  }, 300);

  const clearFilters = () => {
    setFilters({
      status: '',
      search: ''
    });
  };

  const handleViewXml = (registration) => {
    setSelectedRegistration(registration);
    setXmlModalOpen(true);
  };

  const getRegistrationStatusBadgeClass = (status) => {
    if (!status) return 'badge-info';
    
    const statusUpper = status.toUpperCase();
    
    if (statusUpper === 'APPROVED' || statusUpper === 'COMPLETED') {
      return 'badge-success';
    }
    
    if (statusUpper === 'PENDING_APPROVAL' || statusUpper === 'PROCESSING' || statusUpper === 'RECEIVED') {
      return 'badge-warning';
    }
    
    if (statusUpper === 'REJECTED') {
      return 'badge-danger';
    }
    
    return 'badge-info';
  };

  const getRegistrationStatusText = (status) => {
    if (!status) return 'Unknown';
    
    const statusMap = {
      'SIGNED': 'Signed',
      'SENT': 'Sent',
      'RECEIVED': 'Received',
      'PROCESSING': 'Processing',
      'COMPLETED': 'Completed',
      'APPROVED': 'Approved',
      'REJECTED': 'Rejected',
      'PENDING_APPROVAL': 'Pending Approval'
    };
    
    return statusMap[status.toUpperCase()] || status;
  };

  const getAccountStatusBadge = (accountStatus) => {
    if (!accountStatus) return null;
    
    const statusClasses = {
      'ENAB': 'bg-green-100 text-green-800',
      'DSBL': 'bg-red-100 text-red-800',
      'PEND': 'bg-yellow-100 text-yellow-800',
      'SUSP': 'bg-orange-100 text-orange-800'
    };
    
    const statusLabels = {
      'ENAB': 'Enabled',
      'DSBL': 'Disabled',
      'PEND': 'Pending',
      'SUSP': 'Suspended'
    };
    
    const badgeClass = statusClasses[accountStatus] || 'bg-gray-100 text-gray-800';
    const label = statusLabels[accountStatus] || accountStatus;
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badgeClass}`}>
        {label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Account Registrations</h1>
          <p className="text-gray-600 mt-1">View and manage account opening requests (acmt.007)</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadRegistrations}
            disabled={loading}
            className="btn-primary flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          </div>
          <button
            onClick={clearFilters}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Clear All
          </button>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Account ID, Merchant Code, Organization Name..."
                  className="input pl-10"
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                className="select"
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="">All Statuses</option>
                <option value="APPROVED">Approved</option>
                <option value="PENDING_APPROVAL">Pending Approval</option>
                <option value="PROCESSING">Processing</option>
                <option value="REJECTED">Rejected</option>
                <option value="COMPLETED">Completed</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Results ({registrations.length})
            </h2>
          </div>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center space-y-4">
                <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
                <p className="text-gray-600">Loading registrations...</p>
              </div>
            </div>
          ) : registrations.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-900 font-medium mb-2">No registrations found</p>
                <p className="text-gray-600">Try adjusting your filters</p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Organization
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Merchant Code
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account Status
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Signature
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {registrations.map((reg) => (
                    <tr key={reg.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono font-medium text-gray-900">
                          #{reg.id}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-gray-900">
                          {truncate(reg.accountId, 20)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {reg.accountName || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {reg.orgLegalName || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-gray-600">
                          {reg.merchantCode || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {getAccountStatusBadge(reg.accountStatus)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {reg.signatureOk === true ? (
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-success-100" title="Signature valid">
                            <CheckCircle2 className="w-5 h-5 text-success-600" />
                          </span>
                        ) : reg.signatureOk === false ? (
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-danger-100" title="Signature invalid">
                            <XCircle className="w-5 h-5 text-danger-600" />
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`badge ${getRegistrationStatusBadgeClass(reg.status)}`}>
                          {getRegistrationStatusText(reg.statusLabel || reg.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(reg.createdAt, 'PPp')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {/* Quick XML View - only show if rawXml exists */}
                          {reg.rawXml && (
                            <button
                              onClick={() => handleViewXml(reg)}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
                              title="View XML"
                            >
                              <FileCode className="w-4 h-4" />
                            </button>
                          )}
                          <Link
                            to={`/iso20022/registrations/${reg.id}`}
                            className="text-primary-600 hover:text-primary-900 inline-flex items-center space-x-1"
                          >
                            <span>View</span>
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* XML Viewer Modal */}
      {selectedRegistration && (
        <XmlViewerModal
          isOpen={xmlModalOpen}
          onClose={() => {
            setXmlModalOpen(false);
            setSelectedRegistration(null);
          }}
          xmlContent={selectedRegistration.rawXml}
          fileName={`registration-acmt007-${selectedRegistration.id}.xml`}
          title={`Account Registration #${selectedRegistration.id}`}
        />
      )}
    </div>
  );
};

export default AccountRegistrations;
